import { Injectable, NgZone } from '@angular/core';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { BehaviorSubject, debounceTime, fromEvent } from 'rxjs';
import { ExternalSettings } from '../external-settings/external-settings.interface';
import { UIUtilityService } from 'src/app/system/utility/ui-utility.service';
import { MEBrickAppearance } from '../../activities/me-meta';
import { PlatformService } from 'src/app/system/utility/platform.service';
import { OverlayContainer } from '@angular/cdk/overlay';

@Injectable({
  providedIn: 'root',
})
export class ResponsiveDesignService {

  private widthSubject = new BehaviorSubject<number>(window.innerWidth);
  private heightSubject = new BehaviorSubject<number>(window.innerHeight);

  width$ = this.widthSubject.asObservable();
  height$ = this.heightSubject.asObservable();

  constructor(breakpointObserver: BreakpointObserver, private overlayContainer: OverlayContainer, private zone: NgZone) {
    this.checkResponsivenessBSUI(breakpointObserver);
    this.initResizeListener();
  }

  public isDesktop$ = new BehaviorSubject<boolean>(true);
  public isTablet$ = new BehaviorSubject<boolean>(false);
  public isMobile$ = new BehaviorSubject<boolean>(false);

  public isXS$ = new BehaviorSubject<boolean>(false);
  public isS$ = new BehaviorSubject<boolean>(false);
  public isM$ = new BehaviorSubject<boolean>(false);
  public isL$ = new BehaviorSubject<boolean>(false);
  public isXL$ = new BehaviorSubject<boolean>(true);

  private _screenType = new BehaviorSubject<ScreenType>(ScreenType.TypeXL);
  public get screenType(): BehaviorSubject<ScreenType> {
    return this._screenType;
  }

  private showSidebarNav = new BehaviorSubject<boolean>(false);
  private showBottomNav = new BehaviorSubject<boolean>(false);

  public readonly MAX_ABSOLUTE_DIALOG_WIDTH = 800;

  private checkResponsivenessBSUI(breakpointObserver: BreakpointObserver) {
    breakpointObserver
      .observe([
        '(max-width: 575.98px)',
        '(min-width: 576px) and (max-width: 767.98px)',
        '(min-width: 768px) and (max-width: 991.98px)',
        '(min-width: 992px) and (max-width: 1199.98px)',
        '(min-width: 1200px)',
      ])
      .subscribe((result: BreakpointState) => {
        if (breakpointObserver.isMatched('(max-width: 575.98px)')) {
          this._screenType.next(ScreenType.TypeXS);
        } else if (breakpointObserver.isMatched('(min-width: 576px) and (max-width: 767.98px)')) {
          this._screenType.next(ScreenType.TypeS);
        } else if (breakpointObserver.isMatched('(min-width: 768px) and (max-width: 991.98px)')) {
          this._screenType.next(ScreenType.TypeM);
        } else if (breakpointObserver.isMatched('(min-width: 992px) and (max-width: 1199.98px)')) {
          this._screenType.next(ScreenType.TypeL);
        } else if (breakpointObserver.isMatched('(min-width: 1200px)')) {
          this._screenType.next(ScreenType.TypeXL);
        }

        // Update Screen Property
        this.setScreenProperty();
      });
  }

  private setScreenProperty() {
    switch (this.screenType.value) {
      case ScreenType.TypeXS:
        this.setScreenSize(true, false, false, false, false);
        break;
      case ScreenType.TypeS:
        this.setScreenSize(false, true, false, false, false);
        break;
      case ScreenType.TypeM:
        this.setScreenSize(false, false, true, false, false);
        break;
      case ScreenType.TypeL:
        this.setScreenSize(false, false, false, true, false);
        break;
      case ScreenType.TypeXL:
        this.setScreenSize(false, false, false, false, true);
        break;
    }
  }

  private setScreenSize(isXS: boolean, isS: boolean, isM: boolean, isL: boolean, isXL: boolean) {

    const isNative = PlatformService.isMobile();

    // Update Screen Size Type (For fine control)
    this.isXL$.next(isXL);
    this.isL$.next(isL);
    this.isM$.next(isM);
    this.isS$.next(isS);
    this.isXS$.next(isXS);

    if (isNative) {
      this.isMobile$.next(true);
      this.isTablet$.next(false);
      this.isDesktop$.next(false);
      return;
    }

    // Browser-only behavior
    const isDesktop = isXL || isL;
    const isTablet = isM || isS;
    const isMobile = isXS;

    this.applyViewClass(isDesktop ? ViewMode.Desktop : isTablet ? ViewMode.Tablet : ViewMode.Mobile);

    this.isDesktop$.next(isDesktop);
    this.isTablet$.next(isTablet);
    this.isMobile$.next(isMobile);
  }

  private applyViewClass(device: ViewMode): void {
    const body = document.body;
    const container = this.overlayContainer.getContainerElement();
    if (device === ViewMode.Mobile) {
      body.classList.add('view-mode-mobile');
      container?.classList.add('view-mode-mobile');
      body.classList.remove('view-mode-desktop', 'view-mode-tablet');
      container?.classList.remove('view-mode-desktop', 'view-mode-tablet');
    } else if (device === ViewMode.Tablet) {
      body.classList.add('view-mode-tablet');
      container?.classList.add('view-mode-tablet');
      body.classList.remove('view-mode-desktop', 'view-mode-mobile');
      container?.classList.remove('view-mode-desktop', 'view-mode-mobile');
    } else {
      body.classList.add('view-mode-desktop');
      container?.classList.add('view-mode-desktop');
      body.classList.remove('view-mode-mobile', 'view-mode-tablet');
      container?.classList.remove('view-mode-mobile', 'view-mode-tablet');
    }
  }

  public getTableClass() {
    return this.getResponsiveClass(['bg-white', 'font-black'], ['bg-black-mobile', 'font-white']);
  }

  public getFieldBoxClass() {
    return this.getResponsiveClass(['bg-white'], ['bg-black-mobile']);
  }

  public getButtonsPadClass() {
    return this.getResponsiveClass('justify-content-end', 'justify-content-between');
  }

  public getDialogButtonsPadClass() {
    return this.getResponsiveClass('justify-content-center', 'justify-content-between');
  }

  public getPaginatorMobileClass() {
    return this.getResponsiveClass('', 'mobile-paginator');
  }

  public getFontClass() {
    return this.getResponsiveClass('font-black', 'font-white');
  }

  public getSpacing(desktop: SpacingArray, mobile: SpacingArray, type: SpacingType = 'm'): string[] {
    const toClasses = (arr: SpacingArray): string[] => {
      switch (arr.length) {
        case 1:
          return [`${type}-${arr[0]}`]; // all sides
        case 2:
          return [`${type}y-${arr[0]}`, `${type}x-${arr[1]}`]; // top/bottom, left/right
        case 3:
          return [`${type}t-${arr[0]}`, `${type}x-${arr[1]}`, `${type}b-${arr[2]}`]; // Most useful case: top, left/right, bottom
        case 4:
          return [
            `${type}t-${arr[0]}`, // top
            `${type}e-${arr[1]}`, // right
            `${type}b-${arr[2]}`, // bottom
            `${type}s-${arr[3]}`  // left
          ];
        default:
          return [];
      }
    };
    return this.getResponsiveClass(toClasses(desktop), toClasses(mobile));
  }

  public getResponsiveClass(desktopClass: string[] | string, mobileClass: string[] | string = []): string[] {
    let clazzA = Array.isArray(desktopClass) ? desktopClass : [desktopClass];
    let clazzB = Array.isArray(mobileClass) ? mobileClass : [mobileClass];
    return !this.isMobile$.value ? clazzA : clazzB;
  }

  public appendMobileClass(mobileClass: string[] | string = []): string[] {
    return this.getResponsiveClass([], mobileClass);
  }

  public appendDesktopClass(desktopClass: string[] | string = []): string[] {
    return this.getResponsiveClass(desktopClass, []);
  }

  public getResponsiveLottie(desktopFile: string, mobileFile: string) {
    return 'assets/lottie/' + (!this.isMobile$.value ? desktopFile : mobileFile);
  }

  // Compare Group 1: Only Consider the View Type
  public isMobileView(): boolean {
    return this.isMobile$.value;
  }

  public isTabletView(): boolean {
    return this.isTablet$.value;
  }

  public isDesktopView(): boolean {
    return !this.isMobileView();
  }

  // Compare Group 2: Consider the View Type and Platform
  public isMobileBrowser(): boolean {
    return PlatformService.isBrowser() && this.isMobileView();
  }

  public isTabletBrowser(): boolean {
    return PlatformService.isBrowser() && this.isTabletView();
  }

  public isDesktopBrowser(): boolean {
    return PlatformService.isBrowser() && this.isDesktopView();
  }

  public isTabletApp(): boolean {
    return PlatformService.isMobile() && this.isTabletView();
  }

  public isMobileApp(): boolean {
    return PlatformService.isMobile();
  }

  public isMobileAndroid() : boolean {
    return PlatformService.isAndroid() && this.isMobileView();
  }

  // Compare Group 3: Return the View Type 
  public getViewType(): ViewType {
    if (this.isMobileApp()) return ViewType.MobileApp;
    if (this.isMobileBrowser()) return ViewType.MobileBrowser;
    return ViewType.DesktopBrowser;
  }

  public getResponsiveValue<T>(mobileValue: T, desktopValue: T): T {
    return this.isMobile$.value ? mobileValue : desktopValue;
  }

  /**
   * Mobile View will use the Pure CSS to return White color while Desktop mode will respect to user value
   */
  public getTableStyle(externalSettings: ExternalSettings): {} {
    if (!externalSettings) return {};
    if (this.isMobile$.value) return { color: '' };

    return UIUtilityService.getBankTextColor();
  }

  public getMatFormFieldAppearance(): MEBrickAppearance {
    return !this.isMobile$.value ? MEBrickAppearance.OUTLINE : MEBrickAppearance.FILL;
  }

  public setBottomNav(isShow: boolean) {
    this.showBottomNav.next(isShow);
  }

  public isShowBottomNav(): BehaviorSubject<boolean> {
    return this.showBottomNav;
  }

  public getSidebarNav(): BehaviorSubject<boolean> {
    return this.showSidebarNav;
  }
  public setSidebarNav(isShowSidebarNav: boolean): void {
    this.showSidebarNav.next(isShowSidebarNav);
  }

  private initResizeListener() {
    this.zone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(debounceTime(50))
        .subscribe(() => {
          this.zone.run(() => {
            this.updateSize();
          });
        });
    });
  }

  private updateSize() {
    this.widthSubject.next(window.innerWidth);
    this.heightSubject.next(window.innerHeight);
  }

  public get windowWidth(): number {
    return this.widthSubject.value;
  }

  public get windowHeight(): number {
    return this.heightSubject.value;
  }

  public isWindowWidthLessThan(size: number): boolean {
    return this.windowWidth < size;
  }

  public isWindowHeightLessThan(size: number): boolean {
    return this.windowHeight < size;
  }

}

export class CustomDialogWidthConfig {

  private width_XL: string = '30';
  private width_L: string = '40';
  private width_M: string = '60';
  private width_S: string = '90';
  private width_XS: string = '94';

  constructor(private srvRpDesign: ResponsiveDesignService) { }
  /**
   * This function is to return the class name for the dialog panel
   */
  public getDialogWidthClass(override: { XL?: string, L?: string, M?: string, S?: string, XS?: string } = {}): string {
    let widthClass = 'custom-dialog-width-';

    // Override the default value if necessary
    if (override.XL) this.width_XL = override.XL;
    if (override.L) this.width_L = override.L;
    if (override.M) this.width_M = override.M;
    if (override.S) this.width_S = override.S;
    if (override.XS) this.width_XS = override.XS;

    switch (this.srvRpDesign.screenType.value) {
      case ScreenType.TypeXL:
        return widthClass + this.width_XL;
      case ScreenType.TypeXS:
        return widthClass + this.width_XS;
      case ScreenType.TypeL:
        return widthClass + this.width_L;
      case ScreenType.TypeM:
        return widthClass + this.width_M;
      case ScreenType.TypeS:
        return widthClass + this.width_S;
    }
  }
}

export enum ScreenType {
  TypeXS = 'TypeXS',
  TypeS = 'TypeS',
  TypeM = 'TypeM',
  TypeL = 'TypeL',
  TypeXL = 'TypeXL',
}

export enum ViewType {
  DesktopBrowser = 'DesktopBrowser',
  MobileBrowser = 'MobileBrowser',
  MobileApp = 'MobileApp',
}

export enum ViewMode {
  Desktop = 'Desktop',
  Mobile = 'Mobile',
  Tablet = 'Tablet',
}

export type SpacingValue = 0 | 1 | 2 | 3 | 4 | 5;
export type SpacingArray = [SpacingValue] | [SpacingValue, SpacingValue] | [SpacingValue, SpacingValue, SpacingValue] | [SpacingValue, SpacingValue, SpacingValue, SpacingValue];
export type SpacingType = 'm' | 'p'